'use client'

import { useEffect, useState } from 'react'

import { publicGetRequest } from 'services/one'
import { useLeagueContext } from 'utils/context'
import { dateToString, isDeviceTimezoneSpanish } from 'utils/date'
import TableStaticPagination from 'components/TableStaticPagination'
import Spin from 'components/Spin'
import NoResults from 'components/NoResults'
import { GoBackAction } from 'components/GoBack'
import ResultsLeagues from 'components/ResultsLeagues'
import Timetable from 'components/Timetable'
import AlertPending from 'components/AlertPending'
import AlertPostponed from 'components/AlertPostponed'

const Calendar = () => {
  const { divisionFilterSelected, dayFilterSelected, categoryFilterSelected } =
    useLeagueContext()

  const [loading, setLoading] = useState()
  const [results, setResults] = useState([])
  const [matchDaysIdSelected, setMatchDaysIdSelected] = useState()
  const [matchData, setMatchData] = useState()
  const [teamsImgs, setTeamsImgs] = useState({})

  const getPageData = async () => {
    setLoading(true)
    const pageDataReq = await publicGetRequest({
      id: divisionFilterSelected,
      endpoint: 'LEAGUES_CALENDAR'
    })
    const pageData = pageDataReq.filter(
      ({ MatchDay }) => MatchDay === dayFilterSelected
    )
    setResults(pageData)
    setLoading(false)
  }
  useEffect(() => {
    if (divisionFilterSelected) {
      getPageData()
    }
  }, [divisionFilterSelected, dayFilterSelected])

  const getMatchData = async () => {
    const matchDataReq = await publicGetRequest({
      id: matchDaysIdSelected,
      endpoint: 'LEAGUES_MATCH'
    })
    setMatchData(matchDataReq)
  }
  useEffect(() => {
    if (matchDaysIdSelected) {
      getMatchData()
    }
  }, [matchDaysIdSelected])

  useEffect(() => {
    setResults([])
  }, [categoryFilterSelected])

  return (
    <div className="max-w-6xl mx-auto mb-56">
      {loading ? (
        <div className="flex justify-center my-20">
          <Spin color="text-blue" size="10" className="my-2 lg:my-8" />
        </div>
      ) : !results?.length ? (
        <NoResults />
      ) : (
        !matchDaysIdSelected && (
          <div className="">
            <TableStaticPagination
              headers={[`JORNADA ${dayFilterSelected}`]}
              content={results.map((match, idx) => ({
                0: match?.Date
                  ? `${dateToString(match.Date).withYear}${
                      match.Schedule ? ` ${match.Schedule}` : ''
                    } ${isDeviceTimezoneSpanish({ minimal: true })}`
                  : match?.Schedule || '—',
                1: match?.RedPointLocal
                  ? `<div className="flex"><span className="flex-1">${match.LocalTeamName}</span><span className="bg-sec-red rounded-full w-8 h-8 ml-2"></span></div>`
                  : match.LocalTeamName,
                2: `${match.LocalPoints}-${match.AwayPoints}`,
                3: match?.RedPointVisiting
                  ? `<div className="flex"><span className="flex-1">${match.AwayTeamName}</span><span className="bg-sec-red rounded-full w-8 h-8 ml-2"></span></div>`
                  : match.AwayTeamName,
                4: (
                  <div className="flex">
                    {match?.PendingReview ? (
                      <div className="ml-8">
                        <AlertPending onlyIcon />
                      </div>
                    ) : null}
                    {match?.PostponedMatch ? (
                      <div className="ml-8">
                        <AlertPostponed onlyIcon />
                      </div>
                    ) : null}
                  </div>
                ),
                5: `id:${match.MatchDaysId}`
              }))}
              cols={5}
              type="center"
              extraClass={{
                2: 'font-bold'
              }}
              headersMobile={[`JORNADA ${dayFilterSelected}`]}
              contentMobile={results.map((match, idx) => ({
                0: match?.Date
                  ? `${dateToString(match.Date).withYear}<br/>${
                      match.Schedule ? ` ${match.Schedule}` : ''
                    } ${isDeviceTimezoneSpanish({ minimal: true })}`
                  : match?.Schedule || '—',
                1: match?.RedPointLocal
                  ? `<div className="flex"><span className="flex-1">${match.LocalTeamName}</span><span className="bg-sec-red rounded-full w-8 h-8 ml-2"></span></div>`
                  : match.LocalTeamName,
                2: `${match.LocalPoints}-${match.AwayPoints}`,
                3: match?.RedPointVisiting
                  ? `<div className="flex"><span className="flex-1">${match.AwayTeamName}</span><span className="bg-sec-red rounded-full w-8 h-8 ml-2"></span></div>`
                  : match.AwayTeamName,
                4: (
                  <div className="flex">
                    {match?.PendingReview ? (
                      <div className="ml-0">
                        <AlertPending onlyIcon />
                      </div>
                    ) : null}
                    {match?.PostponedMatch ? (
                      <div className="ml-4">
                        <AlertPostponed onlyIcon />
                      </div>
                    ) : null}
                  </div>
                ),
                5: `id:${match.MatchDaysId}`
              }))}
              colsMobile={6}
              extraClassMobile={{
                0: 'text-xs',
                1: 'text-xs col-span-2',
                2: 'font-bold',
                3: 'text-xs col-span-2',
                4: 'absolute'
              }}
              forceRowsInMobile={true}
              setOnClick={(e) => {
                const matchDaysIdClicked =
                  e.target.parentElement.childNodes[0].dataset?.id ||
                  e.target.parentElement.childNodes[0].childNodes[0].dataset?.id
                const resultState = results.find(
                  ({ MatchDaysId }) => `${MatchDaysId}` === matchDaysIdClicked
                )?.resultState
                if (
                  resultState === 'FCP-REVISADO' ||
                  resultState === 'CONFIRMADO'
                ) {
                  setMatchDaysIdSelected(matchDaysIdClicked)
                }
                const matchDetails = results.find(
                  ({ MatchDaysId }) => `${MatchDaysId}` === matchDaysIdClicked
                )
                setTeamsImgs({
                  local: matchDetails?.LocalImage,
                  away: matchDetails?.AwayImage
                })
              }}
              resultsByPage={15}
            />
          </div>
        )
      )}
      {matchDaysIdSelected && matchData && (
        <div className="max-w-6xl mx-auto">
          <div className="py-10">
            <GoBackAction action={() => setMatchDaysIdSelected()} />
          </div>
          <ResultsLeagues
            matches={[matchData.BasicData]}
            teamsImgs={teamsImgs}
            pendingReview={matchData.BasicData?.PendingReview}
            postponedMatch={matchData.BasicData?.PostponedMatch}
          />
          <Timetable
            matches={matchData.Matches.map((match) => ({
              noDate: `Partit ${match.Number}`,
              result: `${match.LocalSet1}-${match.VisitingSet1} / ${
                match.LocalSet2
              }-${match.VisitingSet2} ${
                !(
                  (match.LocalSet1 > match.VisitingSet1 &&
                    match.LocalSet2 > match.VisitingSet2) ||
                  (match.VisitingSet1 > match.LocalSet1 &&
                    match.VisitingSet2 > match.LocalSet2)
                )
                  ? `/ ${match.LocalSet3}-${match.VisitingSet3}`
                  : ''
              }`,
              teams: [
                {
                  name: match.LocalPlayerNameA,
                  imageUrl: match.LocalPlayerImgA
                },
                {
                  name: match.LocalPlayerNameB,
                  imageUrl: match.LocalPlayerImgB
                },
                {
                  name: match.VisitingPlayerNameA,
                  imageUrl: match.VisitingPlayerImgA
                },
                {
                  name: match.VisitingPlayerNameB,
                  imageUrl: match.VisitingPlayerImgB
                }
              ],
              wo: match?.LocalWO || match?.VisitingWO,
              jwo: match?.LocalJWO || match?.VisitingJWO,
              injury: match?.LocalInjury || match?.VisitingInjury,
              won: match?.WinningMatchPlayer
            }))}
          />
        </div>
      )}
    </div>
  )
}

export default Calendar
